import { forwardRef } from 'react';

import cn from '@/utils/cn';

import * as Radio from '@radix-ui/react-radio-group';
import { cva } from 'class-variance-authority';

const radioGroup = cva('', {
  variants: {
    layout: {
      horizontal: [
        'grid grid-cols-1 gap-3 md:grid-cols-2 md:flex-row md:gap-5.5',
      ],
      vertical: ['flex flex-col gap-3'],
    },
  },
});

interface RadioGroupProps {
  layout?: 'horizontal' | 'vertical';
  onChange?: (value: string) => void;
}

const RadioGroup = forwardRef<
  React.ElementRef<typeof Radio.Root>,
  Omit<React.ComponentPropsWithoutRef<typeof Radio.Root>, 'onValueChange'> &
    RadioGroupProps
>(({ className, children, layout = 'vertical', onChange, ...props }, ref) => {
  return (
    <Radio.Root
      className={cn(radioGroup({ layout }), className)}
      ref={ref}
      onValueChange={onChange}
      {...props}
    >
      {children}
    </Radio.Root>
  );
});

RadioGroup.displayName = 'RadioGroup';
export interface Props
  extends React.ComponentPropsWithoutRef<typeof Radio.Root> {
  label: string;
  title?: string;
}

const RadioItem = forwardRef<
  React.ElementRef<typeof Radio.Item>,
  React.ComponentPropsWithoutRef<typeof Radio.Item> & Props
>(({ className, label, value, ...props }, ref) => {
  return (
    <>
      <Radio.Item
        id={props.id}
        ref={ref}
        value={value}
        className={cn(
          'group flex w-full items-center gap-2.5 text-left lg:max-w-[300px]',
          'rounded-lg border border-dark-gray-100 p-4 md:py-[15px]',
          'data-[state=checked]:border-green-500 data-[state=checked]:bg-green-50',
          'transition duration-200 ease-in-out',
          'hover:border-green-300',
          className
        )}
        {...props}
      >
        <div
          className={cn(
            'flex aspect-square size-4.5 items-center rounded-full border border-dark-gray-100 bg-white',
            'group-data-[state=checked]:border-5 group-data-[state=checked]:border-green-500'
          )}
        >
          <Radio.Indicator />
        </div>
        <span
          className={cn(
            'caption-large cursor-pointer text-dark-gray-500 transition-colors',
            'group-data-[state=checked]:text-green-500'
          )}
        >
          {label}
        </span>
      </Radio.Item>
    </>
  );
});

RadioItem.displayName = 'RadioItem';

export { RadioItem };

export default RadioGroup;
